import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Eye, EyeOff, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { workCategories } from "@/lib/work-gallery";
import { addTeamMember, listTeam, removeTeamMember } from "@/lib/work-photos.functions";
import { AdminBookings } from "@/components/site/AdminBookings";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [
    { title: "Booking & Photo Dashboards — IZHUB Cleaning" },
    { name: "description", content: "Manage IZHUB cleaning bookings, operational estimates, photos and videos." },
    { property: "og:title", content: "Booking & Photo Dashboards — IZHUB Cleaning" },
    { property: "og:description", content: "Manage IZHUB cleaning bookings, operational estimates, photos and videos." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex, nofollow" },
  ] }),
  component: AdminPage,
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-x py-24 text-center">
      <h1 className="font-display text-2xl">Dashboard didn't load</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : String(error)}
      </p>
    </div>
  ),
});

type PhotoRow = {
  id: string;
  category: string;
  kind: string;
  src: string | null;
  before_src: string | null;
  after_src: string | null;
  alt: string;
  sort_order: number;
  published: boolean;
};

const PHOTO_URL = (path: string) => `/api/public/work-photo?path=${encodeURIComponent(path)}`;

async function uploadFile(file: File) {
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = `uploads/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("work-photos").upload(path, file, { contentType: file.type });
  if (error) throw new Error(error.message);
  return PHOTO_URL(path);
}

function AdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState<string>("");
  const [tab, setTab] = useState<"bookings" | "photos">("bookings");

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.getUser();
      setEmail(data.user?.email ?? "");
      await supabase.rpc("claim_first_admin");
      await queryClient.invalidateQueries({ queryKey: ["admin-photos"] });
    })();
  }, [queryClient]);

  const photos = useQuery({
    queryKey: ["admin-photos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_photos")
        .select("id, category, kind, src, before_src, after_src, alt, sort_order, published")
        .order("category")
        .order("sort_order");
      if (error) throw new Error(error.message);
      return (data ?? []) as PhotoRow[];
    },
  });

  const team = useQuery({ queryKey: ["admin-team"], queryFn: () => listTeam(), retry: false });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin-photos"] });
    await queryClient.invalidateQueries({ queryKey: ["work-photos"] });
  };

  async function signOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  return (
    <section className="bg-background/90 py-12 backdrop-blur-sm md:py-16">
      <div className="container-x space-y-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Admin portal</h1>
            <p className="mt-2 text-muted-foreground">Signed in as {email}</p>
          </div>
          <Button variant="outline" onClick={signOut}>Sign out</Button>
        </header>

        <div role="tablist" aria-label="Dashboards" className="flex flex-wrap gap-2 border-b border-border pb-3">
          {([
            ["bookings", "Booking dashboard"],
            ["photos", "Photo dashboard"],
          ] as const).map(([id, label]) => (
            <Button
              key={id}
              role="tab"
              aria-selected={tab === id}
              variant={tab === id ? "default" : "outline"}
              onClick={() => setTab(id)}
            >
              {label}
            </Button>
          ))}
        </div>

        {tab === "bookings" ? (
          <AdminBookings />
        ) : (
          <div className="space-y-12">
            <UploadCard onDone={refresh} />

            <div className="space-y-10">
              {workCategories.map((category) => {
                const items = (photos.data ?? []).filter((p) => p.category === category.id);
                return (
                  <div key={category.id}>
                    <h2 className="font-display text-2xl">{category.label} <span className="text-base text-muted-foreground">({items.length})</span></h2>
                    {items.length === 0 ? (
                      <p className="mt-3 text-sm text-muted-foreground">No photos yet for this service.</p>
                    ) : (
                      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {items.map((item) => (
                          <PhotoCard key={item.id} item={item} onChanged={refresh} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <TeamCard team={team.data} error={team.error as Error | null} onChanged={() => queryClient.invalidateQueries({ queryKey: ["admin-team"] })} />
          </div>
        )}
      </div>
    </section>
  );
}

type UploadKind = "photo" | "before-after" | "video" | "before-after-video";

function UploadCard({ onDone }: { onDone: () => Promise<void> }) {
  const [category, setCategory] = useState<string>(workCategories[0]!.id);
  const [kind, setKind] = useState<UploadKind>("photo");
  const [alt, setAlt] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (form: HTMLFormElement) => {
      const fd = new FormData(form);
      const isVideo = kind === "video" || kind === "before-after-video";
      const noun = isVideo ? "video" : "photo";

      if (kind === "photo" || kind === "video") {
        const files = fd.getAll("photos").filter((f): f is File => f instanceof File && f.size > 0);
        if (files.length === 0) throw new Error(`Choose at least one ${noun}.`);
        let i = 0;
        for (const file of files) {
          const url = await uploadFile(file);
          const { error } = await supabase.from("work_photos").insert({
            category,
            kind,
            src: url,
            alt: alt || "IZHUB cleaning work",
            sort_order: Date.now() % 100000 + i++,
            published: true,
          });
          if (error) throw new Error(error.message);
        }
        return `${files.length} ${noun}${files.length > 1 ? "s" : ""} added.`;
      }

      const before = fd.get("before");
      const after = fd.get("after");
      if (!(before instanceof File) || !(after instanceof File) || !before.size || !after.size) throw new Error(`Choose both a before and an after ${noun}.`);
      const [beforeUrl, afterUrl] = await Promise.all([uploadFile(before), uploadFile(after)]);
      const { error } = await supabase.from("work_photos").insert({
        category,
        kind,
        before_src: beforeUrl,
        after_src: afterUrl,
        alt: alt || "Before and after cleaning",
        sort_order: Date.now() % 100000,
        published: true,
      });
      if (error) throw new Error(error.message);
      return `Before-and-after ${noun} pair added.`;
    },
    onSuccess: async (msg) => {
      setStatus(msg);
      setAlt("");
      await onDone();
    },
    onError: (error: Error) => setStatus(error.message),
  });

  return (
    <form
      className="rounded-xl border border-border bg-card p-6 shadow-soft"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate(event.currentTarget);
      }}
    >
      <h2 className="font-display text-2xl">Add photos</h2>
      <div className="mt-5 grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="category">Service</Label>
          <select id="category" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
            {workCategories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="kind">Type</Label>
          <select id="kind" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={kind} onChange={(e) => setKind(e.target.value as UploadKind)}>
            <option value="photo">Single photos</option>
            <option value="before-after">Before and after photo pair</option>
            <option value="video">Single videos</option>
            <option value="before-after-video">Before and after video pair</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="alt">Description (optional)</Label>
          <Input id="alt" value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Cleaned kitchen in Tidworth" />
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {kind === "photo" || kind === "video" ? (
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="photos">{kind === "video" ? "Videos (up to 200MB each)" : "Photos"}</Label>
            <Input id="photos" name="photos" type="file" accept={kind === "video" ? "video/*" : "image/*"} multiple />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="before">Before {kind === "before-after-video" ? "video" : "photo"}</Label>
              <Input id="before" name="before" type="file" accept={kind === "before-after-video" ? "video/*" : "image/*"} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="after">After {kind === "before-after-video" ? "video" : "photo"}</Label>
              <Input id="after" name="after" type="file" accept={kind === "before-after-video" ? "video/*" : "image/*"} />
            </div>
          </>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={mutation.isPending}>
          <Upload className="mr-2 h-4 w-4" />{mutation.isPending ? "Uploading…" : "Upload"}
        </Button>
        {status && <p className="text-sm text-muted-foreground">{status}</p>}
      </div>
    </form>
  );
}

function PhotoCard({ item, onChanged }: { item: PhotoRow; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);

  async function togglePublished() {
    setBusy(true);
    await supabase.from("work_photos").update({ published: !item.published }).eq("id", item.id);
    await onChanged();
    setBusy(false);
  }

  async function remove() {
    if (!window.confirm("Delete this photo?")) return;
    setBusy(true);
    await supabase.from("work_photos").delete().eq("id", item.id);
    await onChanged();
    setBusy(false);
  }

  const isPair = item.kind === "before-after" || item.kind === "before-after-video";
  const isVideo = item.kind === "video" || item.kind === "before-after-video";
  const preview = isPair ? item.after_src : item.src;
  const label = isPair ? (isVideo ? "Before / after video" : "Before / after") : isVideo ? "Video" : null;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background shadow-soft">
      <div className="relative aspect-[4/3] bg-muted">
        {preview && (isVideo
          ? <video src={preview} controls muted playsInline preload="metadata" className="h-full w-full object-cover" />
          : <img src={preview} alt={item.alt} className="h-full w-full object-cover" loading="lazy" />)}
        {label && <span className="absolute left-2 top-2 rounded-full bg-background/90 px-2 py-0.5 text-xs font-semibold">{label}</span>}
        {!item.published && <span className="absolute right-2 top-2 rounded-full bg-foreground/80 px-2 py-0.5 text-xs font-semibold text-background">Hidden</span>}
      </div>
      <div className="space-y-2 p-3">
        <p className="line-clamp-2 text-sm text-muted-foreground">{item.alt}</p>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={togglePublished}>
            {item.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span className="ml-1">{item.published ? "Hide" : "Show"}</span>
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={remove} aria-label="Delete photo">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function TeamCard({ team, error, onChanged }: { team?: { userId: string; role: string; email: string }[]; error: Error | null; onChanged: () => void }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  if (error) return null;

  async function add(event: React.FormEvent) {
    event.preventDefault();
    setStatus(null);
    try {
      await addTeamMember({ data: { email } });
      setEmail("");
      setStatus("Invitation sent.");
      onChanged();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not add that person.");
    }
  }

  async function remove(userId: string) {
    try {
      await removeTeamMember({ data: { userId } });
      onChanged();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not remove that person.");
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
      <h2 className="font-display text-2xl">Who can manage photos</h2>
      <form onSubmit={add} className="mt-4 flex flex-wrap gap-3">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@example.com" className="max-w-xs" />
        <Button type="submit">Invite</Button>
      </form>
      {status && <p className="mt-2 text-sm text-muted-foreground">{status}</p>}
      <ul className="mt-5 space-y-2">
        {(team ?? []).map((member) => (
          <li key={member.userId} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
            <span>{member.email || member.userId} — {member.role}</span>
            {member.role !== "admin" && (
              <Button type="button" size="sm" variant="ghost" onClick={() => remove(member.userId)}>Remove</Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
